/**
 * Badge component for labels and tags
 */

import { clsx } from 'clsx';

interface BadgeProps {
  children: React.ReactNode;
  color?: string;
  variant?: 'solid' | 'subtle' | 'outline' | 'default' | 'secondary' | 'destructive';
  size?: 'sm' | 'md';
  className?: string;
  onClick?: () => void;
  onRemove?: () => void;
}

function Badge({
  children,
  color,
  variant = 'subtle',
  size = 'sm',
  className,
  onClick,
  onRemove,
}: BadgeProps) {
  const baseClasses =
    'inline-flex items-center gap-1 rounded-full font-medium transition-colors';

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  // If custom color is provided, use it directly
  const getColorStyles = () => {
    if (color) {
      if (variant === 'solid') {
        return {
          backgroundColor: color,
          color: getContrastColor(color),
        };
      }
      if (variant === 'subtle') {
        return {
          backgroundColor: `${color}20`,
          color: color,
        };
      }
      if (variant === 'outline') {
        return {
          backgroundColor: 'transparent',
          borderColor: color,
          color: color,
        };
      }
    }
    return {};
  };

  const variantClasses: Record<string, string> = {
    solid: 'bg-primary-600 text-white',
    subtle: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
    outline: 'border border-neutral-300 text-neutral-700 dark:border-neutral-600 dark:text-neutral-300',
    default: 'bg-primary-600 text-white',
    secondary: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
    destructive: 'bg-red-600 text-white',
  };

  const defaultClasses = !color
    ? variantClasses[variant] ?? variantClasses.subtle
    : variant === 'outline'
      ? 'border'
      : '';

  const Component = onClick ? 'button' : 'span';

  return (
    <Component
      className={clsx(
        baseClasses,
        sizeClasses[size],
        defaultClasses,
        onClick && 'cursor-pointer hover:opacity-80',
        className
      )}
      style={getColorStyles()}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
        >
          <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
            <path
              d="M9 3L3 9M3 3l6 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </Component>
  );
}

function getContrastColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

// Both named and default exports for compatibility
export { Badge };
export default Badge;
